import { Check, Copy } from "lucide-react";
import type { ElementType } from "react";
import { forwardRef, useState } from "react";
import { cn } from "@/lib/utils";

// Title Component
export interface TitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
	level?: 1 | 2 | 3 | 4 | 5;
}

const Title = forwardRef<HTMLHeadingElement, TitleProps>(
	({ className, level = 1, children, ...props }, ref) => {
		const levelStyles = {
			1: "text-4xl font-bold",
			2: "text-3xl font-bold",
			3: "text-2xl font-semibold",
			4: "text-xl font-semibold",
			5: "text-lg font-medium",
		};

		const Tag = `h${level}` as ElementType;

		return (
			<Tag
				ref={ref}
				className={cn("text-gray-900 dark:text-gray-100", levelStyles[level], className)}
				{...props}
			>
				{children}
			</Tag>
		);
	},
);

Title.displayName = "Title";

// Text Component
export interface TextProps extends React.HTMLAttributes<HTMLSpanElement> {
	type?: "secondary" | "success" | "warning" | "danger";
	strong?: boolean;
	code?: boolean;
	copyable?: boolean | { text?: string; onCopy?: () => void };
	ellipsis?: boolean;
}

const Text = forwardRef<HTMLSpanElement, TextProps>(
	({ className, type, strong, code, copyable, ellipsis, children, ...props }, ref) => {
		const [copied, setCopied] = useState(false);

		const typeStyles = {
			secondary: "text-gray-500 dark:text-gray-400",
			success: "text-success-500",
			warning: "text-warning-500",
			danger: "text-error-500",
		};

		const handleCopy = async () => {
			const text = typeof copyable === "object" && copyable.text ? copyable.text : String(children);

			await navigator.clipboard.writeText(text);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);

			if (typeof copyable === "object" && copyable.onCopy) {
				copyable.onCopy();
			}
		};

		let content: React.ReactNode = children;

		if (code) {
			content = (
				<code className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono">
					{content}
				</code>
			);
		}

		return (
			<span
				ref={ref}
				className={cn(
					"text-gray-700 dark:text-gray-200",
					type && typeStyles[type],
					strong && "font-semibold",
					ellipsis && "truncate block",
					copyable && "inline-flex items-center gap-1",
					className,
				)}
				{...props}
			>
				{content}
				{copyable && (
					<button
						type="button"
						onClick={handleCopy}
						className="inline-flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
					>
						{copied ? (
							<Check className="h-3.5 w-3.5 text-success-500" />
						) : (
							<Copy className="h-3.5 w-3.5" />
						)}
					</button>
				)}
			</span>
		);
	},
);

Text.displayName = "Text";

// Paragraph Component
export interface ParagraphProps extends Omit<TextProps, "ellipsis"> {
	ellipsis?: boolean | { rows?: number; expandable?: boolean };
}

const Paragraph = forwardRef<HTMLParagraphElement, ParagraphProps>(
	({ className, ellipsis, children, ...props }, ref) => {
		const [expanded, setExpanded] = useState(false);

		const ellipsisConfig =
			typeof ellipsis === "object" ? ellipsis : { rows: ellipsis ? 1 : undefined };

		const ellipsisStyles =
			ellipsisConfig.rows && !expanded
				? {
						display: "-webkit-box",
						WebkitLineClamp: ellipsisConfig.rows,
						WebkitBoxOrient: "vertical" as const,
						overflow: "hidden",
					}
				: {};

		return (
			<div>
				<p
					ref={ref}
					className={cn("text-gray-700 dark:text-gray-200 leading-relaxed", className)}
					style={ellipsisStyles}
					{...props}
				>
					{children}
				</p>
				{ellipsisConfig.expandable && ellipsisConfig.rows && (
					<button
						type="button"
						onClick={() => setExpanded(!expanded)}
						className="text-primary-500 hover:text-primary-600 text-sm mt-1"
					>
						{expanded ? "收起" : "展开"}
					</button>
				)}
			</div>
		);
	},
);

Paragraph.displayName = "Paragraph";

export { Title, Text, Paragraph };
